import {useContext, useState, useCallback} from 'react';
import {observer} from 'mobx-react-lite';
import {NetworkContext} from '../../lib/context';
import './specialty-filter-table.scss';
import Spinner from '../../../../components/spinner';
import ErrorDisplay from '../error-display';
import SpecialtyListItem from './specialty-list-item';
import SpecialtyFilterControls from './specialty-filter-controls';
import {useSpecialtyFilter} from '../hooks/use-specialty-filter';
import {useSpecialtyDataSource} from '../hooks/use-specialty-data-source';
import {Information} from '@carbon/icons-react';
import {Tooltip} from '@carbon/react';

type Props = {
    fullHeight?: boolean;
    specialtyLoading?: boolean;
    specialtyError?: string | null;
};

function SpecialtyFilterTable(props: Props) {
    const {fullHeight, specialtyLoading, specialtyError} = props;
    const store = useContext(NetworkContext);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Get data source (defaultComplianceScores or complianceScores)
    const complianceScores = useSpecialtyDataSource(
        store.defaultComplianceScores,
        store.complianceScores
    );

    // Filter and color utilities
    const {filteredScores, getSpecialtyColor, isSpecialtySelected} = useSpecialtyFilter({
        complianceScores,
        specialtyFilter: store.specialtyFilter,
        searchTerm,
    });

    // Computed values
    const selectedCount = store.getSelectedSpecialtyCount();
    const isMaxReached = !store.canSelectMoreSpecialties();
    const loading = specialtyLoading ?? false;
    const error = specialtyError ?? null;

    // Clear map data when filter changes
    const clearMapData = useCallback(() => {
        store.patientGeoJson = null;
        store.providerGeoJson = null;
    }, [store]);

    // Handle checkbox toggle
    const handleCheckboxChange = useCallback(
        (specialtyCode: string) => {
            const isSelected = isSpecialtySelected(specialtyCode);

            if (!isSelected && isMaxReached) {
                return;
            }

            store.toggleSpecialtyFilter(specialtyCode);
            clearMapData();
        },
        [store, isMaxReached, clearMapData, isSpecialtySelected]
    );

    // Handle clear all
    const handleClearAll = useCallback(() => {
        store.clearSpecialtyFilters();
        clearMapData();
        setSearchTerm('');
    }, [store, clearMapData]);

    // Early returns for loading/error states
    if (!store.readyToLoadData()) return null;
    if (error) return <ErrorDisplay error={error} title="Unable to Load Specialties" />;
    if (loading) {
        return (
            <div className="specialty-filter-loading">
                <Spinner /> Loading specialties...
            </div>
        );
    }

    return (
        <div
            data-testid="specialty-filter-table"
            className={`specialty-filter ${fullHeight ? 'specialty-filter--full-height' : ''}`}
        >
            <h1 className="specialty-filter-title">Access Score by Specialty</h1>

            <div className="specialty-filter-header">
                <div className={`specialty-count ${isMaxReached ? 'specialty-count--max' : ''}`}>
                    {isMaxReached && (
                        <Tooltip label="Deselect a Specialty to add another" align="bottom-left">
                            <button type="button" className="specialty-count-icon-button">
                                <Information size={16} className="specialty-count-icon" />
                            </button>
                        </Tooltip>
                    )}
                    {selectedCount}/{store.maxSpecialtySelection} Specialties Selected
                </div>
            </div>

            <SpecialtyFilterControls
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearAll={handleClearAll}
                isMaxReached={isMaxReached}
                selectedCount={selectedCount}
            />

            <div className="specialty-list-container">
                {complianceScores.length > 0 && (
                    <ul className="specialty-list">
                        {filteredScores.length > 0 ? (
                            filteredScores.map((specialty, i) => {
                                const isSelected = isSpecialtySelected(specialty.specialtyCode);
                                const isDisabled = !isSelected && isMaxReached;

                                return (
                                    <SpecialtyListItem
                                        key={specialty.specialtyCode || i}
                                        specialty={specialty}
                                        isSelected={isSelected}
                                        isDisabled={isDisabled}
                                        color={getSpecialtyColor(specialty.specialtyCode)}
                                        onToggle={handleCheckboxChange}
                                        testId={`specialty-checkbox-option-${i}`}
                                    />
                                );
                            })
                        ) : (
                            <li className="specialty-item">
                                <div className="specialty-empty">
                                    {searchTerm ? 'No specialties match your search' : 'No specialties available'}
                                </div>
                            </li>
                        )}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default observer(SpecialtyFilterTable);
